<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed } from "vue";

import { applyActionParams } from "@/action/actions";
import { boardActions } from "@/action/boardActions";
import { cardActions } from "@/action/cardActions";
import { selectionActions } from "@/action/selectionActions";
import { toggleActions } from "@/action/toggleActions";
import { action, actionMap, actionOptMap } from "@/components/action/action";
import SubMenu from "@/components/ui/DropdownMenu/SubMenu.vue";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import MenuListSubheader from "@/components/ui/MenuList/MenuListSubheader.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { keyboardNavigation } from "@/directives/keyboardNavigation";
import { relativeFromViewport } from "@/math/coordinate-utils";
import type { ViewportCoordinate } from "@/model/coordinates";
import { useBoardStore } from "@/store/board";
import { useSelectionStore } from "@/store/selection";
import { useUserStore } from "@/store/user";
import { useWorkModeStore } from "@/store/workMode";

export type BoardContextMenuMode = "iteration" | "backlog" | "mouse";

const props = defineProps<{ position: ViewportCoordinate; mode: BoardContextMenuMode }>();

defineEmits<{ dummy: [] }>();

const vKeyboardNavSub = keyboardNavigation();

const selectedCount = computed(() => useBoardStore().selectedStickyIds.length);
const editable = computed(() => useUserStore().isAllowed("edit"));
const context = computed(() => useBoardStore().baseBoard.contextActions);
const positional = computed(() =>
  useBoardStore().baseBoard.positionalActions(relativeFromViewport(props.position)),
);
const general = computed(() =>
  actionOptMap({
    newSticky:
      editable.value && props.mode === "mouse"
        ? applyActionParams(cardActions.add, { pos: props.position }, true)
        : undefined,
    screenshot:
      props.mode === "mouse" || props.mode === "backlog" ? boardActions.screenshot : undefined,
  }),
);
const anyGeneral = computed(() => Object.values(general.value).some((value) => value));
const { isExecutionMode } = storeToRefs(useWorkModeStore());
const isTeamBoard = computed(() => useBoardStore().board?.type === "team");
const canShowKanbanOrMyWork = computed(() => !isTeamBoard.value || isExecutionMode.value);

const board = computed(() => actionOptMap(context.value.board));
const region = computed(() => actionOptMap(positional.value.region?.actions));
const selection = actionMap(selectionActions);
</script>

<template>
  <MenuList>
    <MenuListItems role="menu" style="width: 300px">
      <template v-if="anyGeneral">
        <MenuListSubheader>{{ $t("contextMenu.general") }}</MenuListSubheader>
        <MenuListItem v-if="general.newSticky" v-bind="general.newSticky" />
        <MenuListItem v-if="general.screenshot" v-bind="general.screenshot" />
        <MenuListItem
          v-if="canShowKanbanOrMyWork && board.kanban"
          v-bind="board.kanban"
          :icon="null"
        />
        <MenuListItem v-if="board.syncProgramBacklog" v-bind="board.syncProgramBacklog" />
        <MenuListItem v-if="board.draw" v-bind="board.draw" :icon="null" />
      </template>

      <template v-if="editable && selectedCount > 1 && context.selection">
        <SubMenu :offset="{ left: 250, top: -35 }" width="md">
          <template #trigger>
            <MenuListItem :text="$t('contextMenu.selectedStickyCount', { selectedCount })">
              <template #after>
                <SvgIcon name="arrow/chevron-right" width="20" />
              </template>
            </MenuListItem>
          </template>
          <MenuList>
            <MenuListItems v-keyboard-nav-sub role="menu">
              <MenuListItem v-bind="action(toggleActions.showSelectionOverview)" />
              <template v-if="context.selection.organize">
                <MenuListItem v-bind="selection.alignHorizontal" />
                <MenuListItem v-bind="selection.distributeHorizontal" />
                <MenuListItem v-bind="selection.alignVertical" />
                <MenuListItem v-bind="selection.distributeVertical" />
                <MenuListItem v-bind="selection.lineup" />
                <MenuListItem v-bind="selection.arrange" />
                <MenuListItem v-bind="selection.stack" />
              </template>
              <MenuListItem
                v-if="context.selection.mirror"
                v-bind="selection.openMirrorModal"
                :disabled="useSelectionStore().mirrorTargets.length === 0"
              />
              <MenuListItem v-if="context.selection.team" v-bind="selection.openTeamModal" />
              <MenuListItem v-if="context.selection.art" v-bind="selection.openArtModal" />
              <MenuListItem
                v-if="context.selection.link"
                v-bind="action(cardActions.openLinkModal)"
              />
              <MenuListItem
                v-bind="selection.share"
                :text="$t('label.actionMenu.stickiesLinkToClipboard')"
              />
            </MenuListItems>
          </MenuList>
        </SubMenu>
      </template>

      <template v-if="positional.region">
        <MenuListSubheader>{{ positional.region.name }}</MenuListSubheader>
        <MenuListItem v-if="region.overview" v-bind="region.overview" />
        <MenuListItem
          v-if="canShowKanbanOrMyWork && region.kanban"
          v-bind="region.kanban"
          :icon="null"
        />
        <MenuListItem v-if="region.zoom" v-bind="region.zoom" />
        <MenuListItem v-if="region.sync" v-bind="region.sync" />
        <MenuListItem v-if="region.arrange" v-bind="region.arrange" />
      </template>
    </MenuListItems>
  </MenuList>
</template>
